import { DataTableColumn } from "@/components/ui/DataTable/types";
import { z } from "zod";
import { zodPhoneNumberRefine } from "@/lib/validation/phoneNumber";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/api/utils";

export enum ShirtSize {
  Small = "S",
  Medium = "M",
  Large = "L",
  XLarge = "XL",
  TwoXL = "2XL",
}

export const TRIGGER_TYPES = {
  NEW_CUSTOMER: "customer_onboarding",
  NEW_NON_CUSTOMER: "non_customer_onboarding",
  NEW_DESTINATION: "destination_setup",
  EXITING_CLIENT: "exiting_client",
} as const;

export type TriggerType = typeof TRIGGER_TYPES[keyof typeof TRIGGER_TYPES];

export const newCustomerSchema = z.object({
  trigger_type: z.literal(TRIGGER_TYPES.NEW_CUSTOMER),
  template_id: z.string().min(1, "Template is required"),
  customer_name: z.string().min(1, "Customer name is required"),
  customer_contact_info: z.string().min(1, "Customer contact info is required"),
  corn_cfr_check: z.boolean(),
  canola_cfr_check: z.boolean(),
  number_of_acres: z.number().int(),
  partner_names: z.array(z.string().trim().min(1)).default([]),
  shirt_sizes: z.array(z.enum([ShirtSize.Small, ShirtSize.Medium, ShirtSize.Large, ShirtSize.XLarge, ShirtSize.TwoXL])).default([]),
  bol_sequence: z.string().trim().optional().default(""),
  trader_id: z.string().uuid().optional().nullable().default(null),
  trader_name: z.string().optional().nullable().default(null),
  email: z.string().email().optional().nullable().default(null),
  phone_number: z.string().optional().nullable().default(null),
  mailing_address_line_one: z.string().optional(),
  mailing_address_line_two: z.string().optional(),
  mailing_city: z.string().optional(),
  mailing_state: z.string().optional(),
  mailing_zipcode: z.string().optional(),
  mailing_country: z.enum(["USA", "CANADA"]).optional(),
  mailing_county: z.string().optional(),
  customer_address_line_one: z.string().optional(),
  customer_address_line_two: z.string().optional(),
  customer_city: z.string().optional(),
  customer_state: z.string().optional(),
  customer_zipcode: z.string().optional(),
  customer_country: z.enum(["USA", "CANADA"]).optional(),
  customer_county: z.string().optional(),
  bank_institution_name: z.string().optional().default(""),
  banker_name: z.string().optional().default(""),
  banker_phone: z.string().optional().nullable().default(null),
  banker_email: z.string().email().optional().nullable().default(null),
  has_lienholder: z.boolean().default(false),
  ach_form_country_type: z.enum(["USA", "CAD"]).default("USA"),
  schema_version: z.literal("v2"),
  schema_type: z.literal("new_customer_form"),
  notes: z.string(),
}).refine((data) => data.partner_names.length === data.shirt_sizes.length, {
  message: "Shirt sizes must match number of partners",
  path: ["shirt_sizes"],
}).refine(zodPhoneNumberRefine("phone_number"), {
  message: "Please enter a valid phone number",
  path: ["phone_number"],
}).refine(zodPhoneNumberRefine("banker_phone"), {
  message: "Please enter a valid banker phone number",
  path: ["banker_phone"],
}).refine((data) => {
  if (!data.has_lienholder) { return true; }
  if (!data.bank_institution_name || !data.banker_email || !data.banker_phone) {
    return false;
  }

  return true;
}, {
  message: "Missing bank info or contact info",
  path: ["bank_institution_name"],
});

export const newNonCustomerSchema = z.object({
  trigger_type: z.literal(TRIGGER_TYPES.NEW_NON_CUSTOMER),
  template_id: z.string().min(1, "Template is required"),
  customer_name: z.string().min(1, "Customer name is required"),
  customer_contact_info: z.string().min(1, "Customer contact info is required"),
  corn_cfr_check: z.boolean(),
  canola_cfr_check: z.boolean(),
  number_of_partners: z.number().int(),
  number_of_bols_needed: z.number().int(),
  email: z.string().email("Please enter a valid email address").min(1, "Email is required"),
  mailing_address_line_one: z.string().optional(),
  mailing_address_line_two: z.string().optional(),
  mailing_city: z.string().optional(),
  mailing_state: z.string().optional(),
  mailing_zipcode: z.string().optional(),
  mailing_country: z.enum(["USA", "CANADA"]).optional(),
  mailing_county: z.string().optional(),
  customer_address_line_one: z.string().optional(),
  customer_address_line_two: z.string().optional(),
  customer_city: z.string().optional(),
  customer_state: z.string().optional(),
  customer_zipcode: z.string().optional(),
  customer_country: z.enum(["USA", "CANADA"]).optional(),
  customer_county: z.string().optional(),
  bank_institution_name: z.string().optional().default(""),
  banker_name: z.string().optional().default(""),
  banker_phone: z.string().optional().nullable().default(null),
  banker_email: z.string().email().optional().nullable().default(null),
  has_lienholder: z.boolean().default(false),
  ach_form_country_type: z.enum(["USA", "CAD"]).default("USA"),
  schema_version: z.literal("v2"),
  schema_type: z.literal("new_non_customer_form"),
  notes: z.string(),
}).refine(zodPhoneNumberRefine("banker_phone"), {
  message: "Please enter a valid banker phone number",
  path: ["banker_phone"],
}).refine((data) => {
  if (!data.has_lienholder) { return true; }
  if (!data.bank_institution_name || !data.banker_email || !data.banker_phone) {
    return false;
  }

  return true;
}, {
  message: "Missing bank info or contact info",
  path: ["bank_institution_name"],
});

export const newDestinationSchema = z.object({
  trigger_type: z.literal(TRIGGER_TYPES.NEW_DESTINATION),
  template_id: z.string().min(1, "Template is required"),
  customer_name: z.string().min(1, "Destination name is required"),
  customer_contact_info: z.string().min(1, "Contact info is required"),
  email: z.string().email().optional().nullable().default(null),
  phone_number: z.string().optional().nullable().default(null),
  mailing_address_line_one: z.string().optional(),
  mailing_address_line_two: z.string().optional(),
  mailing_city: z.string().optional(),
  mailing_state: z.string().optional(),
  mailing_zipcode: z.string().optional(),
  mailing_country: z.enum(["USA", "CANADA"]).optional(),
  mailing_county: z.string().optional(),
  customer_address_line_one: z.string().optional(),
  customer_address_line_two: z.string().optional(),
  customer_city: z.string().optional(),
  customer_state: z.string().optional(),
  customer_zipcode: z.string().optional(),
  customer_country: z.enum(["USA", "CANADA"]).optional(),
  customer_county: z.string().optional(),
  schema_version: z.literal("v1"),
  schema_type: z.literal("new_destination"),
  notes: z.string(),
}).refine(zodPhoneNumberRefine("phone_number"), {
  message: "Please enter a valid phone number",
  path: ["phone_number"],
});


export const existingClientSchema = z.object({
  trigger_type: z.literal(TRIGGER_TYPES.EXITING_CLIENT),
  template_id: z.string().min(1, "Template is required"),
  customer_name: z.string().min(1, "Client name is required"),
  customer_contact_info: z.string().default(""),
  exit_date: z.string().min(1, "Exit date is required"),
  notes: z.string(),
  schema_version: z.literal("v1"),
  schema_type: z.literal("exiting_client_form"),
});

export const shirtSizeOptions = [
  { value: ShirtSize.Small, label: "Small (S)" },
  { value: ShirtSize.Medium, label: "Medium (M)" },
  { value: ShirtSize.Large, label: "Large (L)" },
  { value: ShirtSize.XLarge, label: "X-Large (XL)" },
  { value: ShirtSize.TwoXL, label: "2X-Large (2XL)" },
];

export const FormMetaDataSchema = z.union([newCustomerSchema, newNonCustomerSchema, newDestinationSchema, existingClientSchema]);

export type NewCustomerFormData = z.infer<typeof newCustomerSchema>;
export type NewNonCustomerFormData = z.infer<typeof newNonCustomerSchema>;
export type NewDestinationFormData = z.infer<typeof newDestinationSchema>;
export type ExistingClientFormData = z.infer<typeof existingClientSchema>;
export type FormData = z.infer<typeof FormMetaDataSchema>;


export const META_DATA_CUSTOMER_INFO_KEYS = [
  "customer_name", "customer_address", "customer_contact_info"
];

export const META_DATA_KEY_EXCLUDE_LIST = [
  "on_empty_auto_assign_action", "triggered_by_user_id", "trigger_type", "template_id", "trader_id",
  "schema_version", "schema_type",
  "customer_address_line_one", "customer_address_line_two", "customer_city", "customer_state", "customer_zipcode", "customer_country", "customer_county",
  "mailing_address_line_one", "mailing_address_line_two", "mailing_city", "mailing_state", "mailing_zipcode", "mailing_country", "mailing_county",
  "exit_date",
  ...META_DATA_CUSTOMER_INFO_KEYS
];

export function doesInstanceMetaHaveCustomerInfo(meta_data: Record<string, any>) {
  return META_DATA_CUSTOMER_INFO_KEYS.some((key) => Object.keys(meta_data).includes(key));
}

export function generateTriggerMetaDataColumns(trigger_type?: string) {
  const base_customer_info: DataTableColumn<any>[] = [
    {
      header: "Name",
      accessorFn: (row) => row.template_instance?.trigger_meta_data?.customer_name ?? "-",
      size: 120,
    },
    {
      header: "Address",
      enableTooltip: true,
      accessorFn: (row) => {
        const meta = row.template_instance?.trigger_meta_data;
        if (!meta) return "-";
        
        // Check if new structured format (v2)
        if (meta.schema_version === "v2" || ["destination_setup"].includes(meta?.trigger_type || "")) {
          return formatCustomerAddress(meta);
        }
        
        // Fallback to old single-field format
        return meta.customer_address ?? "-";
      },
      cell: (info) => {
        const content = info.getValue();
        if (!content || typeof content !== 'string' || !content.includes('/')) {
          return content;
        }

        return content.split('\n').reduce((acc, line, index, arr) => {
          acc.push(line.trim());
          if (index < arr.length - 1) {
            acc.push(<br />);
          }
          return acc;
        }, [] as any[]);
      },
      size: 120,
    },
    {
      header: "Contact Info",
      enableTooltip: true,
      accessorFn: (row) => row.template_instance?.trigger_meta_data?.customer_contact_info ?? "-",
      cell: (info) => {
        const content = info.getValue();
        if (!content || typeof content !== 'string' || !content.includes('\n')) {
          return content;
        }

        return content.split('\n').reduce((acc, line, index, arr) => {
          acc.push(line);
          acc.push(<br />);
          return acc;
        }, [] as any[]);
      },
      size: 120,
    },
  ] as DataTableColumn<any>[];

  const cfr_checks = [
    {
      header: "Corn CFR",
      accessorFn: (row) => {
        const check = row.template_instance?.trigger_meta_data?.corn_cfr_check;
        return check === true ? "Yes" : "No";
      },
      size: 120,
    },
    {
      header: "Canola CFR",
      accessorFn: (row) => {
        const check = row.template_instance?.trigger_meta_data?.canola_cfr_check;
        return check === true ? "Yes" : "No";
      },
      size: 120,
    },
  ] as DataTableColumn<any>[];

  const remaining_base = [
    {
      header: "Mailing Address",
      accessorFn: (row) => {
        const meta = row.template_instance?.trigger_meta_data;
        if (!meta) return "-";
        
        // Check if new structured format (v2)
        if (meta.schema_version === "v2" || ["destination_setup"].includes(meta?.trigger_type || "")) {
          return formatMailingAddress(meta);
        }
        
        // Fallback to old single-field format
        return meta.mailing_address || "--";
      },
      cell: (info) => {
        const content = info.getValue();
        if (!content || typeof content !== 'string' || !content.includes('/')) {
          return content;
        }

        return content.split('\n').reduce((acc, line, index, arr) => {
          acc.push(line.trim());
          if (index < arr.length - 1) {
            acc.push(<br />);
          }
          return acc;
        }, [] as any[]);
      },
      enableTooltip: true,
      size: 120,
    },
    {
      header: "Notes",
      accessorFn: (row) => row.template_instance?.trigger_meta_data?.notes || "-",
      enableTooltip: true,
      size: 120,
    },
  ] as DataTableColumn<any>[];

  switch (trigger_type || "") {
    case TRIGGER_TYPES.NEW_CUSTOMER:
      return [
        ...base_customer_info,
        ...cfr_checks,
        {
          header: "# of Acres",
          accessorFn: (row) => row.template_instance?.trigger_meta_data?.number_of_acres,
          size: 120,
        },
        { header: "Client Name and Additional Partner Names", accessorFn: (row) => (row.template_instance?.trigger_meta_data?.partner_names || []).join(", ") || "-", size: 200 },
        { header: "Shirt Sizes", accessorFn: (row) => (row.template_instance?.trigger_meta_data?.shirt_sizes || []).join(", ") || "-", size: 160 },
        { header: "BOL Seq", accessorFn: (row) => row.template_instance?.trigger_meta_data?.bol_sequence || "-", size: 120 },
        { header: "Trader", accessorFn: (row) => row.template_instance?.trigger_meta_data?.trader_name || "-", size: 160 },
        { header: "Email", accessorFn: (row) => row.template_instance?.trigger_meta_data?.email || "-", size: 180 },
        { header: "Phone", accessorFn: (row) => row.template_instance?.trigger_meta_data?.phone_number || "-", size: 180 },
        ...remaining_base
      ] as DataTableColumn<any>[];
    case TRIGGER_TYPES.NEW_NON_CUSTOMER:
      return [
        ...base_customer_info,
        ...cfr_checks,
        {
          header: "# of BOLs Needed",
          accessorFn: (row) => row.template_instance?.trigger_meta_data?.number_of_bols_needed,
          size: 120,
        },
        {
          header: "# of Patners",
          accessorFn: (row) => row.template_instance?.trigger_meta_data?.number_of_partners,
          size: 120,
        },
        ...remaining_base
      ] as DataTableColumn<any>[];
    case TRIGGER_TYPES.NEW_DESTINATION:
      return [
        ...base_customer_info,
        {
          header: "Email",
          accessorFn: (row) => row.template_instance?.trigger_meta_data?.email || "-",
          size: 180,
        },
        {
          header: "Phone",
          accessorFn: (row) => row.template_instance?.trigger_meta_data?.phone_number || "-",
          size: 160,
        },
        ...remaining_base
      ] as DataTableColumn<any>[];
    case TRIGGER_TYPES.EXITING_CLIENT:
      return [
        {
          header: "Client Name",
          accessorFn: (row) => row.template_instance?.trigger_meta_data?.customer_name ?? "-",
          size: 120,
        },
        {
          header: "Exit Date",
          accessorFn: (row) => row.template_instance?.trigger_meta_data?.exit_date ?? "",
          columnFilterType: 'date',
          filterDateTimezone: CommonDestinationTimezone,
          filterDisplayFn: (v: unknown) => v ? moment.tz(v as string, CommonDestinationTimezone).format("YYYY-MM-DD") : "-",
          filterParseDateFn: (v: unknown) => v ? moment.tz(v as string, CommonDestinationTimezone) : null,
          cell: (info) => {
            const exitDate = info.getValue() as string;
            if (!exitDate) return "-";
            return moment.tz(exitDate, CommonDestinationTimezone).format("YYYY-MM-DD");
          },
          size: 120,
        },
        {
          header: "Notes",
          accessorFn: (row) => row.template_instance?.trigger_meta_data?.notes || "-",
          enableTooltip: true,
          size: 120,
        },
      ] as DataTableColumn<any>[];
    default:
      return [];
  }

}

export function formatCustomerAddress({
  customer_address,
  customer_address_line_one, customer_address_line_two, customer_city, customer_state, customer_zipcode, customer_country, customer_county
}: {
  customer_address?: string;
  customer_address_line_one: string, customer_address_line_two: string, customer_city: string, customer_state: string, customer_zipcode: string, customer_country: string; customer_county?: string
}) {
  return formatAddress({ address_line_one: customer_address_line_one, address_line_two: customer_address_line_two, city: customer_city, state: customer_state, zipcode: customer_zipcode, country: customer_country, county: customer_county }) || customer_address;
}

export function formatMailingAddress({
  mailing_address,
   mailing_address_line_one, mailing_address_line_two, mailing_city, mailing_state, mailing_zipcode, mailing_country, mailing_county
  }: {
    mailing_address?: string;
    mailing_address_line_one: string, mailing_address_line_two: string, mailing_city: string, mailing_state: string, mailing_zipcode: string, mailing_country: string; mailing_county?: string
  }) {
  return formatAddress({ address_line_one: mailing_address_line_one, address_line_two: mailing_address_line_two, city: mailing_city, state: mailing_state, zipcode: mailing_zipcode, country: mailing_country, county: mailing_county }) || mailing_address ;
}

export function formatAddress({
  address_line_one,
  address_line_two,
  city,
  state,
  zipcode,
  country,
  county,
}: { address_line_one: string, address_line_two: string, city: string, state: string, zipcode: string, country: string, county?: string }) {
  const parts = [];
  if (address_line_one) parts.push(address_line_one);
  if (address_line_two) parts.push(address_line_two);
  if (city || state || zipcode) {
    const cityStateZip = [city, state, zipcode].filter(Boolean).join(" ");
    if (cityStateZip) parts.push(cityStateZip);
  }
  if (country) parts.push(country);
  if (county) parts.push(county)
  return parts.length > 0 ? parts.join(" ") : "";
}
